/**
 * Single redacting boundary for routing HANDLED (caught) errors to Sentry
 * (OPS-513, part of OPS-397 errors→Linear).
 *
 * Frontend Sentry (`main.jsx`) only captures UNCAUGHT errors automatically.
 * A caught-and-toasted API failure or a boundary-caught render crash never
 * reaches Sentry → no Linear card is filed. The existing pipelines that DO see
 * those handled failures (the error-report relay and the activity tracker) have
 * no redaction and never ticket. This helper closes that gap: it captures the
 * handled error to Sentry — the only pipeline that files cards — while applying
 * `redactSensitive` to the extra context first, so a token / JWT / PII echoed in
 * the context can never reach Sentry.
 *
 * This MIRRORS the backend `captureHandled(err, eventKey, extra)` convention
 * (`coordinator/vendor/instrument.js`), which is the standardized half of this
 * same OPS-397 initiative: a STABLE FINGERPRINT `['frontend', eventKey]` collapses
 * every occurrence of one event class into ONE Better Stack error group → ONE
 * Linear card, regardless of IDs/status codes embedded in the message. Without it,
 * Sentry's default message+stack grouping fans a recurring handled error (e.g. a
 * flush failure that retries every 15s, or an API error string carrying a URL/ID)
 * into many groups → a card storm. The fingerprint is why no per-call-site manual
 * throttle is needed.
 *
 * Sentry's `environment` tag (uat / production / development) is set once at
 * `Sentry.init()` in `main.jsx` from the hostname, so every capture is tagged
 * automatically — no per-call env handling needed.
 *
 * SCOPE NOTE: despite the name, this module is the frontend's error-ROUTING policy
 * boundary, not just the Sentry adapter. Alongside `captureHandled` it owns the
 * shared filter lists that decide what is worth reporting at all —
 * `TRANSIENT_NETWORK_ERRORS`, `scrubEventUrls` and `BROWSER_EXTENSION_URLS` — some of
 * which are consumed by non-Sentry pipelines. Importing a filter list from here does
 * NOT wire a caller to Sentry; only calling `captureHandled` does.
 *
 * If Sentry was not initialized (no `VITE_SENTRY_DSN`, e.g. local dev or tests),
 * `captureException` is a safe no-op, so callers can invoke this unconditionally
 * alongside their existing relay / log call. This NEVER throws — a telemetry
 * helper must not be able to crash the catch block it runs in.
 */

import * as Sentry from '@sentry/react'
import { redactRawText, redactSensitive, scrubUrlString } from './redact'

// Breadcrumb `data` fields that carry a URL (navigation `to`/`from`, fetch/xhr
// `url`). Hoisted to module scope so it is not re-allocated per breadcrumb.
const URL_BREADCRUMB_FIELDS = ['url', 'to', 'from']

/**
 * `beforeSend` / `beforeSendTransaction` hook that scrubs live-credential values
 * out of every URL an outbound Sentry event carries, before it leaves the browser
 * (OPS-803). Sentry auto-attaches the current URL to `event.request.url` and the
 * SPA route to navigation breadcrumbs (`data.url` / `data.to` / `data.from`) — so
 * a `token_hash` (or any OAuth/magic-link token) in the address bar would ship to
 * Sentry (and its Session Replay) intact. This is generic: it covers any future
 * single-use-token URL via the shared `URL_SECRET_PARAMS` denylist in `redactUrl`,
 * with no per-route handling. Breadcrumb `to`/`from` values are RELATIVE, which is
 * exactly why `redactUrl` uses string surgery rather than `new URL()`.
 *
 * Returns the (mutated) event so it is still delivered — this only rewrites URL
 * strings, it never drops events. Wrapped in try/catch: a telemetry hook must
 * never throw and suppress error delivery.
 * @param {import('@sentry/react').Event} event
 * @returns {import('@sentry/react').Event}
 */
export function scrubEventUrls(event) {
  if (!event || typeof event !== 'object') return event
  try {
    if (typeof event.request?.url === 'string') {
      event.request.url = scrubUrlString(event.request.url)
    }
    if (Array.isArray(event.breadcrumbs)) {
      for (const crumb of event.breadcrumbs) {
        const data = crumb?.data
        if (!data || typeof data !== 'object') continue
        for (const field of URL_BREADCRUMB_FIELDS) {
          if (typeof data[field] === 'string') data[field] = scrubUrlString(data[field])
        }
      }
    }
  } catch {
    // Never let URL scrubbing break event delivery.
  }
  return event
}

const redactStr = (v) => (typeof v === 'string' ? redactRawText(v) : v)

// Redact one stack frame's `vars` (SDKs that capture locals) and its URL fields.
function scrubFrame(frame) {
  if (!frame || typeof frame !== 'object') return
  if (frame.vars) frame.vars = redactSensitive(frame.vars)
  for (const f of ['filename', 'abs_path']) {
    if (typeof frame[f] === 'string') frame[f] = scrubUrlString(frame[f])
  }
}

function scrubStack(st) {
  if (st && Array.isArray(st.frames)) st.frames.forEach(scrubFrame)
}

// URL-bearing fields of span / trace `data` (OTel + Sentry semantic conventions).
const URL_DATA_FIELDS = [
  'url', 'http.url', 'url.full', 'url.query', 'http.query', 'http.fragment',
  'server.address', 'http.target', 'url.path', 'http.route',
]

function scrubUrlData(data) {
  if (!data || typeof data !== 'object') return
  for (const f of URL_DATA_FIELDS) {
    if (typeof data[f] === 'string') data[f] = scrubUrlString(data[f])
  }
}

// Transaction events carry the raw URL in span descriptions and trace data.
// Scrubs `request.url`-like strings in spans[] and contexts.trace.data. Mutates.
function scrubTransactionFields(event) {
  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      if (!span || typeof span !== 'object') continue
      if (typeof span.description === 'string') span.description = scrubUrlString(span.description)
      scrubUrlData(span.data)
    }
  }
  scrubUrlData(event.contexts?.trace?.data)
}

// Redact every free-text / structured field of an event (OPS-1470). Mutates.
function redactEventFields(event) {
  if (Array.isArray(event.exception?.values)) {
    for (const ex of event.exception.values) {
      if (!ex || typeof ex !== 'object') continue
      ex.value = redactStr(ex.value)
      ex.type = redactStr(ex.type)
      scrubStack(ex.stacktrace)
    }
  }
  scrubStack(event.stacktrace)
  if (Array.isArray(event.threads?.values)) {
    for (const t of event.threads.values) scrubStack(t?.stacktrace)
  }
  event.message = redactStr(event.message)
  if (event.logentry && typeof event.logentry === 'object') {
    event.logentry.message = redactStr(event.logentry.message)
    if (Array.isArray(event.logentry.params)) {
      event.logentry.params = event.logentry.params.map((p) => redactSensitive(p))
    }
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      if (!crumb || typeof crumb !== 'object') continue
      crumb.message = redactStr(crumb.message)
      if (crumb.data != null) crumb.data = redactSensitive(crumb.data)
    }
  }
  for (const k of ['extra', 'contexts', 'tags']) {
    if (event[k] != null) event[k] = redactSensitive(event[k])
  }
  if (typeof event.transaction === 'string') event.transaction = scrubUrlString(event.transaction)
  scrubTransactionFields(event)
}

/**
 * Full `beforeSend` / `beforeSendTransaction` scrubber (OPS-1470): URL scrubbing
 * (`scrubEventUrls`) PLUS value-shape redaction of exception values/types (incl.
 * chained causes), messages, breadcrumbs, extra, contexts, tags and stack-frame
 * vars, so no raw token/PII reaches Better Stack or the Linear cards it files.
 *
 * Never throws. Fails closed: if redaction throws, the sensitive fields are
 * dropped rather than returning the raw event.
 * @param {import('@sentry/react').Event} event
 * @returns {import('@sentry/react').Event}
 */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event
  scrubEventUrls(event)
  try {
    redactEventFields(event)
  } catch {
    try {
      const marker = '[redacted: scrub failed]'
      if (Array.isArray(event.exception?.values)) {
        for (const ex of event.exception.values) {
          if (!ex || typeof ex !== 'object') continue
          ex.value = marker
          delete ex.stacktrace
        }
      }
      if (event.message !== undefined) event.message = marker
      delete event.logentry
      delete event.breadcrumbs
      delete event.extra
      delete event.contexts
      delete event.tags
      delete event.stacktrace
      delete event.threads
      delete event.spans
    } catch {
      // Last resort: never throw out of a telemetry hook.
    }
  }
  return event
}

/**
 * Transient client-network noise to drop at `Sentry.init({ ignoreErrors })`
 * (OPS-688). These are browser-thrown `fetch()` rejections from offline / CORS /
 * DNS / abort-on-navigation / extension interference — NOT real app bugs. With no
 * ignore-list every such blip auto-files a Triage card via the Errors→Linear rule.
 *
 * One string per engine's wording for the same failure: Chrome `TypeError: Failed
 * to fetch`, Firefox `NetworkError when attempting to fetch resource`, Safari
 * `Load failed`. `ignoreErrors` substring-matches the exception value, so the bare
 * `'Failed to fetch'` also catches the `TypeError:`-prefixed Chrome form.
 *
 * Deliberately tight: it matches ONLY these raw network-layer messages. The app's
 * handled API failures surface with their own messages (`fetchWithAuth` rewraps a
 * dead connection as `'Connection issue — please retry.'`; HTTP errors as
 * `HTTP <status>` / `Session expired…`), none of which contain these strings — so
 * real handled errors still report. Do not broaden this list.
 */
export const TRANSIENT_NETWORK_ERRORS = [
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Load failed',
]

/**
 * Browser-extension script origins to drop at `Sentry.init({ denyUrls })`
 * (OPS-1017, re-fingerprinted duplicate OPS-1021). A third-party extension
 * injected into the page throws inside its OWN script; the browser still routes
 * that through `window.onerror`, so Sentry captures it as if it were ours and the
 * Errors→Linear rule auto-files a Triage card for a bug in code we do not ship,
 * cannot read and cannot fix. The reported call site was
 * `chrome-extension://eppiocemhmnlbhjplcgkofciiegomcon/executors/200.js` — not
 * our extension (`opsyncpro-extension` ships no `executors/` directory), and
 * `M_ID` appears nowhere in this repo.
 *
 * This is `denyUrls` — matched against the SCRIPT URL — and deliberately NOT
 * `ignoreErrors`. The OPS-1017 message, "Cannot read properties of undefined
 * (reading 'M_ID')", is a generic TypeError shape our own code produces too;
 * `ignoreErrors` substring-matches the exception VALUE, so filtering on that
 * message class would be a live footgun. `TRANSIENT_NETWORK_ERRORS` above says
 * "Do not broaden this list" for exactly this reason, and
 * `sentry-capture.transientNetwork.test.js` explicitly asserts that "Cannot read
 * properties of undefined (reading 'map')" must NOT be ignored. Provenance — which
 * script threw — is the actual discriminator between their bug and ours, so filter
 * on that.
 *
 * Semantics, per Sentry's `eventFilters` integration: the deny decision is made on
 * the THROWING frame, not on "does this stack contain any app frames". Sentry
 * orders frames oldest-first and `_getLastValidUrl` walks them BACKWARDS (skipping
 * `<anonymous>` and `[native code]`), so the last frame — the one that threw —
 * decides. Consequence: our code calling INTO an extension that then throws is
 * dropped, while an extension calling into our code that then throws is still
 * reported. That is the intended trade-off; the throwing script owns the bug.
 *
 * These are anchored RegExps, not bare strings, deliberately: `denyUrls` treats a
 * string as a SUBSTRING match, which would silently deny any future URL merely
 * containing "chrome-extension" (e.g. an `/assets/chrome-extension-setup-*.js`
 * route chunk). `ms-browser-extension://` (legacy EdgeHTML) is omitted on purpose,
 * not by oversight: that engine is dead and Chromium Edge uses `chrome-extension://`.
 * Sentry's own `detectBrowserExtension.js` still lists it, for browsers we do not
 * support.
 */
export const BROWSER_EXTENSION_URLS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-web-extension:\/\//,
]

/**
 * Does this script URL belong to a browser extension?
 *
 * The shared predicate for every NON-Sentry consumer of the list above (the
 * error-report relay and the activity tracker). Sentry cannot use it — its
 * `denyUrls` option takes the raw `Array<string|RegExp>` — so the array stays
 * exported and this wraps it.
 *
 * The nullish coercion is the load-bearing part and the reason this is a function
 * rather than a copy-pasted `.some(...)` at each call site: an error with NO
 * filename (a same-origin throw the browser could not attribute) coerces to `''`,
 * matches no anchored pattern, and is therefore REPORTED. "Unknown provenance"
 * must fail OPEN — dropping it would silently swallow real app errors. Defining
 * that once means the two pipelines cannot drift apart on it.
 *
 * @param {string | null | undefined} url - a script URL, e.g. `ErrorEvent.filename`.
 * @returns {boolean} true if the URL is extension-origin and should be dropped.
 */
export function isBrowserExtensionUrl(url) {
  return BROWSER_EXTENSION_URLS.some((re) => re.test(url || ''))
}

/**
 * Capture a handled error to Sentry with a stable per-event-class fingerprint
 * and redacted extra context.
 *
 * @param {unknown} err - the caught value. Non-Errors are coerced so grouping
 *   never degrades to `toString` junk.
 * @param {string} eventKey - stable event-class identifier (reuse the Pino/log
 *   event key at the same site, e.g. `'react-error-boundary'`). Fingerprints to
 *   one Better Stack group per class. A falsy key skips the capture — a handled
 *   error with no grouping key would storm cards, so it is dropped by design.
 * @param {Record<string, unknown>} [context] - extra triage context. Walked by
 *   `redactSensitive` so tokens, JWTs, emails and PII are stripped before they
 *   reach Sentry's `extra`.
 */
export function captureHandled(err, eventKey, context = {}) {
  if (!eventKey) return
  try {
    const error =
      err instanceof Error
        ? err
        : new Error(typeof err === 'string' ? err : JSON.stringify(err ?? 'unknown').slice(0, 500))
    Sentry.withScope((scope) => {
      scope.setFingerprint(['frontend', eventKey])
      scope.setTag('event_key', eventKey)
      scope.setTag('capture', 'handled')
      scope.setExtras(redactSensitive(context))
      Sentry.captureException(error)
    })
  } catch {
    // A telemetry helper must never throw inside the catch block that called it.
  }
}

// Replay includes URL strings in meta hrefs, network spans, navigation events,
// and DOM attributes. Scrub all strings so new SDK URL fields are covered too.
export function scrubReplayEvent(event) {
  function walk(value, depth = 0) {
    if (typeof value === 'string') return scrubUrlString(value)
    if (value === null || typeof value !== 'object') return value
    if (depth > 100) throw new Error('Replay nesting limit')
    if (Array.isArray(value)) return value.map((item) => walk(item, depth + 1))
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, walk(item, depth + 1)]))
  }
  try { return walk(event) } catch { return null }
}

/**
 * Global event processor for Session Replay's `replay_event` envelope item.
 * `beforeSend` / `beforeSendTransaction` are NOT invoked for replay events (the
 * replay integration builds them via `prepareEvent`, which runs scope + global
 * event processors and the `preprocessEvent` hook only), so `urls[]` and
 * `request.url` would otherwise ship raw. Register with
 * `Sentry.addEventProcessor(replayEventProcessor)`. Non-replay events pass
 * through untouched (they are covered by `beforeSend*`). Never throws.
 * @param {import('@sentry/react').Event} event
 * @returns {import('@sentry/react').Event}
 */
export function replayEventProcessor(event) {
  if (!event || typeof event !== 'object' || event.type !== 'replay_event') return event
  try {
    if (Array.isArray(event.urls)) event.urls = event.urls.map((u) => scrubUrlString(u))
    scrubEvent(event)
  } catch {
    // Never let scrubbing break delivery.
  }
  return event
}
